import { Button, Card, Col, Divider, Rate, Row, Tabs, Tag, Tooltip } from "antd";
import { Area, Column, Pie } from "@ant-design/charts";
import { CarFilled, CarOutlined, CheckCircleOutlined, CheckOutlined, DoubleRightOutlined, EyeFilled, EyeOutlined, GlobalOutlined, LikeOutlined, LineChartOutlined, PhoneFilled, PhoneOutlined, SearchOutlined, ShopOutlined, TrophyFilled, TrophyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import moment from "moment";
import TableLZM from "../../../components/design/table/TableLZM";
import { LocationCardCompacted } from "../../../components/model/business/FieldsGroup";
import { TableFilter } from "../../../../common/utils/FilterUtils";
import { CaretIncrement, CaretIncrementPercent, Increment } from "../../../../common/utils/Increment";
import { DrawerContext } from "../../../../store/DrawerContext/DrawerContext";

export const BlockConversion = (props: {data: any, selectedType: string}) => {    

    const { t } = useTranslation();
    const { setLocationViewAdnConversionStatsDrawer} = useContext(DrawerContext);

    const map: any = {
        'BUSINESS_IMPRESSIONS_DESKTOP': 'Escritorio',
        'BUSINESS_IMPRESSIONS_MOBILE': 'Móvil',
        'BUSINESS_DIRECTION_REQUESTS': 'Total',
        'CALL_CLICKS': 'Llamadas',
        'WEBSITE_CLICKS': 'Visitas a la web'
    };

    const viewsChartConfig: any = {
        data: props.data?.viewsChart,
        isStack: true,
        xField: 'x',
        yField: 'value',
        seriesField: 'category',
        xAxis: {
			label: {
				formatter: (text: any) => {
					return moment(text, 'YYYY-MM').format('MMM YYYY');					
				},
				autoHide: true,
				autoRotate: true,
			},
		},
        meta: {
            category: {
                formatter: (value: string | number) => {
                    const translationKey = map[value];
                    return translationKey ? t(translationKey) : value;
                },
            },

        },
        color: ['#80DEEA', '#1E88E5']
    };

    const clicksChartConfig: any = {
        data: props.data?.clicksChart,
        isStack: true,
        xField: 'x',
        yField: 'value',
        seriesField: 'category',
        xAxis: {
			label: {
				formatter: (text: any) => {
					return moment(text, 'YYYY-MM').format('MMM YYYY');					
				},
				autoHide: true,
				autoRotate: true,
			},
		},
        meta: {
            category: {
                formatter: (value: string | number) => {
                    const translationKey = map[value];
                    return translationKey ? t(translationKey) : value;
                },
            },

        },
        color: ['#85C1E9', '#01579B']
    };

    const directionsChartConfig: any = {
        data: props.data?.directionsChart,
        isStack: true,
        xField: 'x',
        yField: 'value',
        seriesField: 'category',
        xAxis: {
			label: {
				formatter: (text: any) => {
					return moment(text, 'YYYY-MM').format('MMM YYYY');					
				},
				autoHide: true,
				autoRotate: true,
			},
		},
        meta: {
            category: {
                formatter: (value: string | number) => {
                    const translationKey = map[value];
                    return translationKey ? t(translationKey) : value;
                },
            },

        },
        color: ['#66ACF0']
    };

    const configSearchMaps: any = {
        appendPadding: 10,
        data: props.data?.platformRatioChart,
        angleField: 'value',
        colorField: 'x',
        radius: 0.75,
        color: ['#7FB3D5', '#2663F0'],
        label: {
            type: 'inner',
            offset: '-30%',
            content: (obj: any) => `${obj.value}%`,
            style: {
                fontSize: 14,
                textAlign: 'center',
            },
        },
        interactions: [
            {
                type: 'element-selected',
            },
            {
                type: 'element-active',
            },
        ],
    };

    const columns: any = [	        
		{			
            title: <b style={{color:'grey'}}>Establecimiento</b>,
			key: 'location',				
			width: '300px',					
			sorter: (a: any, b: any) => a.locality?.localeCompare(b.locality),
			filterDropdown: TableFilter,
			filterIcon: () => {
			  return <SearchOutlined />;
			},
			onFilter: (value: any, record: any) => {
				return record.location?.title?.toLowerCase().includes(value?.toLowerCase()) 
				|| record.location?.administrativeArea?.toLowerCase().includes(value?.toLowerCase())
				|| record.location?.locality?.toLowerCase().includes(value?.toLowerCase());
			},
			render: (text: string, record: any) => (
				<LocationCardCompacted location={record.location}/>									
			)
		},			
        {
			title: <div style={{color:'grey', borderBottom:'solid 1px grey'}}>VISIBILIDAD</div>,
			key: 'numberOfViews',			
			align: 'center',
            children: [
                {
                    title: <><EyeFilled/> Impresiones</>,
                    key: 'numberOfViews',			
                    align: 'center',
                    sorter: (a: any, b: any) => b.visibilityMetrics?.numberOfViews - a.visibilityMetrics?.numberOfViews,
                    render: (text: string, record: any) => (   
                        <Tooltip title={<>
                            <div>Periodo actual: {record.visibilityMetrics.numberOfViews}</div>                        
                            {record.visibilityMetrics.numberOfViews > 0 && <div>Un año antes: {record.visibilityMetrics.previousNumberOfViews}</div>}
                        </>}>             
                            <p>
                                <big>{record.visibilityMetrics.numberOfViews}</big>
                                {record.visibilityMetrics.previousNumberOfViews > 0 &&                    
                                <div>
                                    <small style={{color:'steelblue'}}><CaretIncrementPercent value={100 * (record.visibilityMetrics.numberOfViews - record.visibilityMetrics.previousNumberOfViews) / record.visibilityMetrics.previousNumberOfViews} numberOfDecimals={1}/></small>
                                </div>
                                }
                            </p>				
                        </Tooltip>
                    )			
                }
            ]
        },
        {
			title: <div style={{color:'grey', borderBottom:'solid 1px grey'}}>CONVERSIÓN</div>,
			key: 'numberOfDirections',			
			align: 'center',
            children: [
                {
                    title: <><CarFilled/> Cómo Llegar</>,
                    key: 'numberOfDirections',			
                    align: 'center',
                    sorter: (a: any, b: any) => b.visibilityMetrics?.numberOfDirections - a.visibilityMetrics?.numberOfDirections,
                    render: (text: string, record: any) => (
                        <Tooltip title={<>
                            <div>Periodo actual: {record.visibilityMetrics.numberOfDirections}</div>                        
                            {record.visibilityMetrics.previousNumberOfDirections > 0 && <div>Un año antes: {record.visibilityMetrics.previousNumberOfDirections}</div>}
                        </>}>             
                        <span style={{color:'DarkSlateGray'}}>
                            <b>{record.visibilityMetrics.numberOfDirections}</b>
                            {record.visibilityMetrics.previousNumberOfDirections > 0 && 
                            <div>
                                <small style={{color:'steelblue'}}><CaretIncrementPercent value={100 * (record.visibilityMetrics.numberOfDirections - record.visibilityMetrics.previousNumberOfDirections) / record.visibilityMetrics.previousNumberOfDirections} numberOfDecimals={1}/></small>
                            </div>
                            }
                        </span>	
                        </Tooltip>			
                    )			
                },			
                {
                    title: <><PhoneFilled/> Llamadas</>,
                    key: 'numberOfCalls',			
                    align: 'center',
                    sorter: (a: any, b: any) => b.visibilityMetrics?.numberOfCalls - a.visibilityMetrics?.numberOfCalls,
                    render: (text: string, record: any) => (
                        <Tooltip title={<>
                            <div>Periodo actual: {record.visibilityMetrics.numberOfCalls}</div>                        
                            {record.visibilityMetrics.previousNumberOfCalls > 0 && <div>Un año antes: {record.visibilityMetrics.previousNumberOfCalls}</div>}
                        </>}>             
                        <span style={{color:'DarkSlateGray'}}>
                            <b>{record.visibilityMetrics.numberOfCalls}</b>
                            {record.visibilityMetrics.previousNumberOfCalls > 0 && 
                            <div>
                                <small style={{color:'steelblue'}}><CaretIncrementPercent value={100 * (record.visibilityMetrics.numberOfCalls - record.visibilityMetrics.previousNumberOfCalls) / record.visibilityMetrics.previousNumberOfCalls} numberOfDecimals={1}/></small>
                            </div>
                            }
                        </span>		
                        </Tooltip>		
                    )			
                },			
                {
                    title: <><GlobalOutlined/> Visitas Web</>,
                    key: 'numberOfWeb',			
                    align: 'center',
                    sorter: (a: any, b: any) => b.visibilityMetrics?.numberOfWeb - a.visibilityMetrics?.numberOfWeb,
                    render: (text: string, record: any) => (
                        <Tooltip title={<>
                            <div>Periodo actual: {record.visibilityMetrics.numberOfWeb}</div>                        
                            {record.visibilityMetrics.previousNumberOfWeb > 0 && <div>Un año antes: {record.visibilityMetrics.previousNumberOfWeb}</div>}
                        </>}>             
                        <span style={{color:'DarkSlateGray'}}>
                            <b>{record.visibilityMetrics.numberOfWeb}</b>
                            {record.visibilityMetrics.previousNumberOfWeb > 0 && 
                            <div>
                                <small style={{color:'steelblue'}}><CaretIncrementPercent value={100 * (record.visibilityMetrics.numberOfWeb - record.visibilityMetrics.previousNumberOfWeb) / record.visibilityMetrics.previousNumberOfWeb} numberOfDecimals={1}/></small>
                            </div>
                            }
                        </span>	
                        </Tooltip>			
                    )			
                },	
            ]
        },        
		{
			title: '',
			key: 'buttons',			
			align: 'center',
			render: (text: string, record: any) => (				
				<div style={{display:'flex', justifyContent:'space-between'}}>	
					<Button size='small' type='link' onClick={()=>{setLocationViewAdnConversionStatsDrawer({location: record.location, selectedType: props.selectedType})}}>    
						<i>Ver detalle {<DoubleRightOutlined/>}</i>
					</Button>
				</div>
			)
		}		
	]

    return (
        <>             
            {props.data?.blockAggregate && <><Row gutter={16} style={{ marginBottom: 8 }}>
                <Col span={8}>
                    <Card bodyStyle={{ backgroundColor: 'white', borderRadius: '16px', height: 88, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <b style={{ fontSize: 28, lineHeight: 1 }}><EyeOutlined /> {props.data.visibilityMetrics.numberOfViews}</b>
                                <Tooltip title={<>
                                    <div>Periodo actual: {props.data.visibilityMetrics.numberOfViews}</div>
                                    {props.data.visibilityMetrics.previousNumberOfViews > 0 && <div>Un año antes: {props.data.visibilityMetrics.previousNumberOfViews}</div>}
                                </>}>
                                    <span>
                                        {props.data.visibilityMetrics.previousNumberOfViews > 0 &&
                                            <CaretIncrementPercent value={100 * (props.data.visibilityMetrics.numberOfViews - props.data.visibilityMetrics.previousNumberOfViews) / props.data.visibilityMetrics.previousNumberOfViews} numberOfDecimals={1}/>
                                        }
                                    </span>
                                </Tooltip>
                            </span>
                            <span style={{ fontSize: 11, color: '#9F9F9F', fontWeight: 500, lineHeight: 1.2, marginTop: 2 }}>Visibilidad · impresiones</span>
                        </div>
                    </Card>
                </Col>
                <Col span={16}>
                    <Card bodyStyle={{ backgroundColor: 'white', borderRadius: '16px', height: 88, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-around', width: '100%', alignItems: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <b style={{ fontSize: 28, lineHeight: 1 }}><CarOutlined /> {props.data.visibilityMetrics.numberOfDirections}</b>
                                    <Tooltip title={<>
                                        <div>Periodo actual: {props.data.visibilityMetrics.numberOfDirections}</div>
                                        {props.data.visibilityMetrics.previousNumberOfDirections > 0 && <div>Un año antes: {props.data.visibilityMetrics.previousNumberOfDirections}</div>}
                                    </>}>
                                        <span>
                                            {props.data.visibilityMetrics.previousNumberOfDirections > 0 &&
                                                <CaretIncrementPercent value={100 * (props.data.visibilityMetrics.numberOfDirections - props.data.visibilityMetrics.previousNumberOfDirections) / props.data.visibilityMetrics.previousNumberOfDirections} numberOfDecimals={1}/>
                                            }
                                        </span>
                                    </Tooltip>
                                </span>
                                <span style={{ fontSize: 11, color: '#9F9F9F', fontWeight: 500, lineHeight: 1.2, marginTop: 2 }}>Cómo llegar</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <b style={{ fontSize: 28, lineHeight: 1 }}><PhoneOutlined /> {props.data.visibilityMetrics.numberOfCalls}</b>
                                    <Tooltip title={<>
                                        <div>Periodo actual: {props.data.visibilityMetrics.numberOfCalls}</div>
                                        {props.data.visibilityMetrics.numberOfCalls > 0 && <div>Un año antes: {props.data.visibilityMetrics.previousNumberOfCalls}</div>}
                                    </>}>
                                        <span>
                                            {props.data.visibilityMetrics.previousNumberOfCalls > 0 &&
                                                <CaretIncrementPercent value={100 * (props.data.visibilityMetrics.numberOfCalls - props.data.visibilityMetrics.previousNumberOfCalls) / props.data.visibilityMetrics.previousNumberOfCalls} numberOfDecimals={1}/>
                                            }
                                        </span>
                                    </Tooltip>
                                </span>
                                <span style={{ fontSize: 11, color: '#9F9F9F', fontWeight: 500, lineHeight: 1.2, marginTop: 2 }}>Llamadas</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <b style={{ fontSize: 28, lineHeight: 1 }}><GlobalOutlined /> {props.data.visibilityMetrics.numberOfWeb}</b>
                                    <Tooltip title={<>
                                        <div>Periodo actual: {props.data.visibilityMetrics.numberOfWeb}</div>
                                        {props.data.visibilityMetrics.previousNumberOfWeb > 0 && <div>Un año antes: {props.data.visibilityMetrics.previousNumberOfWeb}</div>}
                                    </>}>
                                        <span>
                                            {props.data.visibilityMetrics.previousNumberOfWeb > 0 &&
                                                <CaretIncrementPercent value={100 * (props.data.visibilityMetrics.numberOfWeb - props.data.visibilityMetrics.previousNumberOfWeb) / props.data.visibilityMetrics.previousNumberOfWeb} numberOfDecimals={1}/>
                                            }
                                        </span>
                                    </Tooltip>
                                </span>
                                <span style={{ fontSize: 11, color: '#9F9F9F', fontWeight: 500, lineHeight: 1.2, marginTop: 2 }}>Visitas a la web</span>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>                  

            <h2 style={{color:'slategrey'}}>Visibilidad </h2>
            
             <Row gutter={36} style={{ marginBottom: '20px' }}>
                    <Col span={16}>
                        <Card bodyStyle={{ backgroundColor: 'white', borderRadius: '25px', height: '327px' }} style={{ border: 'none' }}>
                            <div className="title-charts">
                                <h1><EyeFilled/> Impresiones</h1>
                            </div>
                            <div style={{ padding: '25px 0px 25px 25px' }}>
                                <Column {...viewsChartConfig} height={200} loading={false} />
                            </div>
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card bodyStyle={{ backgroundColor: 'white', borderRadius: '25px', height: '327px' }} style={{ border: 'none' }}>
                            <div className="title-charts">
                                <h1>Maps vs Search</h1>
                            </div>
                            <div style={{ padding: '25px 0px 25px 25px' }}>
                                <Pie {...configSearchMaps} height={200} loading={false} />
                            </div>
                        </Card>
                    </Col>
                </Row>
                <h2 style={{color:'slategrey'}}>Conversión</h2>                
                
                <Row gutter={36} style={{ marginBottom: '20px' }}>
                    <Col span={12}>
                        <Card bodyStyle={{ backgroundColor: 'white', borderRadius: '25px', height: '327px' }} style={{ border: 'none' }}>
                            <div className="title-charts">
                                <h1><CarFilled style={{fontSize:'17px'}}/> Cómo llegar</h1>
                            </div>
                            <div style={{ padding: '25px 0px 25px 25px' }}>
                                <Area {...directionsChartConfig} height={200} loading={false} />
                            </div>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card bodyStyle={{ backgroundColor: 'white', borderRadius: '25px', height: '327px' }} style={{ border: 'none' }}>

                            <div className="title-charts">
                                <h1><PhoneFilled style={{fontSize:'17px'}}/> Llamadas + <GlobalOutlined/> Visitas Web </h1>
                            </div>

                            <div style={{ padding: '25px 0px 25px 25px' }}>
                                <Column {...clicksChartConfig} height={200} loading={false} />
                            </div>

                        </Card>
                    </Col>
                </Row>     
                {/*<h2 style={{color:'slategrey'}}>Métricas por local</h2>*/}
                </>}
                {props.data?.blockListVisible &&                
                    <>
                        <TableLZM 
                            rowKey='location.composeId'
                            scroll={{ x: true, y: 800 }}						
                            columns={columns}
                            dataSource={props.data?.locationsWithMetrics}
                            loading={false}/>
                    </>}
				{/*props.showButtons && <CSVDownload dataSource={transformarDatos(props.data)} title="Descargar CSV" fileName={`Datos de reseñas por local.csv`}/>			*/}
        </>
    )
}